import type { QueryClient } from "@tanstack/react-query";
import type {
  InvestmentResponse,
  InvestmentUpdateRequest,
  InvestmentWithDetailsResponse,
} from "@/domain/investment";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject } from "../cache/cacheSlots";
import { aggregateSlot, flatAggregates } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import { pipelineMetrics } from "./pipelineMetricsCache";
import { investmentPage, investmentRules, INVESTMENT_STATE_ORDER } from "./investmentRules";
import { viewSlot } from "../cache/listViews";

export type InvestmentPatch = Partial<InvestmentResponse>;

type Row = InvestmentWithDetailsResponse;

function isWithDetails(value: unknown): value is Row {
  return isObject(value) && isObject(value.investment) && typeof value.investment.uuid === "string";
}

const merge = (record: Row, patch: InvestmentPatch): Row => ({
  ...record,
  investment: { ...record.investment, ...patch },
});

const isFilled = (value: string | null | undefined) => (value ?? "").trim() !== "";

const aggregates = flatAggregates<Row>({
  sums: { "amount:sum": (row) => row.investment.amount },
  counts: {
    ...Object.fromEntries(
      INVESTMENT_STATE_ORDER.map((state) => [
        `state:${state}`,
        (row: Row) => row.investment.state === state,
      ]),
    ),
    "beneficiary:filled": (row) => isFilled(row.investment.beneficiary?.name),
    "beneficiary:empty": (row) => !isFilled(row.investment.beneficiary?.name),
    "fiscal_quota:filled": (row) => !!row.investment.quota,
    "fiscal_quota:empty": (row) => !row.investment.quota,
  },
  ratios: { "state:pct_funded": (row) => row.investment.state === "funded" },
});

export const investmentCache = defineEntityCache<string, InvestmentPatch, Row>({
  entity: InvalidationEntity.INVESTMENT,
  row: merge,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.investments.lists(orgId),
      shape: investmentPage,
      merge,
      parse: queryKeys.investments.parseList,
      rules: investmentRules,
    }),
    viewSlot({
      prefix: queryKeys.investments.kanbanColumns(orgId),
      shape: investmentPage,
      merge,
      parse: queryKeys.investments.parseKanbanColumn,
      rules: investmentRules,
    }),
    aggregateSlot({
      prefix: queryKeys.investments.aggregatesAll(orgId),
      rules: investmentRules,
      parse: queryKeys.investments.parseAggregates,
      ...aggregates,
      sizeFrom: {
        prefix: queryKeys.investments.lists(orgId),
        parse: queryKeys.investments.parseList,
        total: (page) => (investmentPage.isPage(page) ? investmentPage.total(page) : undefined),
      },
    }),
    aggregateSlot({
      prefix: queryKeys.pipeline.metricsAll(orgId),
      rules: investmentRules,
      parse: queryKeys.pipeline.parseMetrics,
      ...pipelineMetrics,
    }),
    detailSlot({
      prefix: queryKeys.investments.details(orgId),
      isRecord: isWithDetails,
      idOf: investmentPage.idOf,
      merge,
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.investments.detail(orgId, uuid),
    queryKeys.investments.documents(orgId, uuid),
  ],
});

/** The fields of an update that the card can show before the server answers. */
export function toInvestmentPatch(data: InvestmentUpdateRequest): InvestmentPatch {
  return {
    ...(data.state !== undefined ? { state: data.state } : {}),
    ...(data.amount !== undefined ? { amount: data.amount } : {}),
    ...(data.return_percentage !== undefined ? { return_percentage: data.return_percentage } : {}),
  };
}

/** The investment as any list or board already holds it, to seed its detail. */
export function findCachedInvestment(
  client: QueryClient,
  orgId: string,
  uuid: string,
): InvestmentWithDetailsResponse | undefined {
  return investmentCache.at(orgId).change(client, uuid, null).before;
}
